import { registerSW } from 'virtual:pwa-register';
import { environment } from '@/config/environment';

type UpdateServiceWorkerFn = (reloadPage?: boolean) => Promise<void>;

interface PwaRegistrationState {
  needRefresh: boolean;
  offlineReady: boolean;
}

let updateServiceWorker: UpdateServiceWorkerFn | null = null;
let registrationState: PwaRegistrationState = {
  needRefresh: false,
  offlineReady: false,
};

const listeners = new Set<(state: PwaRegistrationState) => void>();

function notifyListeners(): void {
  listeners.forEach((listener) => listener(registrationState));
}

export function initializePwaRegistration(): void {
  if (!environment.pwa.enabled || !('serviceWorker' in navigator) || updateServiceWorker) {
    return;
  }

  updateServiceWorker = registerSW({
    immediate: true,
    onNeedRefresh() {
      registrationState = { ...registrationState, needRefresh: true };
      notifyListeners();
      window.location.reload();
    },
    onOfflineReady() {
      registrationState = { ...registrationState, offlineReady: true };
      notifyListeners();
    },
    onRegisterError(error) {
      console.error('[PWA] Service worker registration failed:', error);
    },
  });

  // Check for SW updates every 15 minutes
  setInterval(
    () => {
      void refreshServiceWorkerRegistration();
    },
    15 * 60 * 1000
  );
}

export function subscribeToPwaRegistrationState(
  listener: (state: PwaRegistrationState) => void
): () => void {
  listeners.add(listener);
  listener(registrationState);

  return () => {
    listeners.delete(listener);
  };
}

export async function refreshServiceWorkerRegistration(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) {
    return;
  }

  await registration.update();
}

export async function activateServiceWorkerUpdate(): Promise<boolean> {
  if (!registrationState.needRefresh || !updateServiceWorker) {
    return false;
  }

  await updateServiceWorker(true);
  registrationState = { ...registrationState, needRefresh: false };
  notifyListeners();

  return true;
}

export async function clearServiceWorkerRegistrations(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map((registration) => registration.unregister()));
}

export async function clearCacheStorage(): Promise<void> {
  if (!('caches' in window)) {
    return;
  }

  const cacheNames = await caches.keys();
  await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
}

export async function disableServiceWorkerCaching(): Promise<void> {
  await clearServiceWorkerRegistrations();
  await clearCacheStorage();
  resetPwaRegistrationState();
}

export function resetPwaRegistrationState(): void {
  registrationState = {
    needRefresh: false,
    offlineReady: false,
  };
  updateServiceWorker = null;
  notifyListeners();
}
